'use client';

import { IBusinessType } from '@/interfaces';
import { getThemeBackgroundClass, getThemeColorValue } from '@/lib/theme';
import { ITheme } from '@/types/theme';
import { useEffect, useMemo, useState } from 'react';
import BusinessTypeCard from './BusinessTypeCard';
import WaveDivider from './WaveDivider';

interface BusinessTypesCarouselProps {
  allBusinessTypes: IBusinessType[];
  slugs: string[];
  language: 'en' | 'zh';
  theme?: ITheme;
  title?: string;
  subtitle?: string;
  backgroundColor?: 'primary' | 'secondary';
}

export default function BusinessTypesCarousel({
  allBusinessTypes,
  slugs,
  language,
  theme = null,
  title,
  subtitle,
  backgroundColor = 'secondary',
}: BusinessTypesCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const themeColor = getThemeColorValue(theme);

  // Filter and sort business types based on slugs
  const businessTypes = useMemo(() => {
    const filtered = allBusinessTypes.filter(type =>
      slugs.includes(type.help_center_article_extra_json.slug)
    );
    return filtered.sort(
      (a, b) =>
        slugs.indexOf(a.help_center_article_extra_json.slug) -
        slugs.indexOf(b.help_center_article_extra_json.slug)
    );
  }, [allBusinessTypes, slugs]);

  // Handle responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, businessTypes.length - itemsPerView);

  const goToSlide = (index: number) => {
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
  };

  const goToPrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      goToNext();
    }
    if (touchStart - touchEnd < -75) {
      goToPrev();
    }
  };

  if (businessTypes.length === 0) {
    return null;
  }

  const slideWidth = 100 / itemsPerView;
  const bgClasses = getThemeBackgroundClass(theme, backgroundColor);

  // Default titles
  const defaultTitle =
    language === 'en' ? 'Perfect For These Business Types' : '適合這些業務類型';
  const defaultSubtitle =
    language === 'en'
      ? 'See how other businesses use this product'
      : '看看其他企業如何使用此產品';

  return (
    <section className={`${bgClasses} py-16 md:py-24 relative`}>
      {/* Wave at top */}
      <WaveDivider
        position="top"
        backgroundColor={backgroundColor}
        theme={theme}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-[var(--color-foreground)] mb-4">
            {title || defaultTitle}
          </h2>
          <p className="text-lg text-[var(--color-foreground-secondary)] leading-relaxed">
            {subtitle || defaultSubtitle}
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Left Arrow */}
          {maxIndex > 0 && (
            <button
              onClick={goToPrev}
              disabled={currentIndex === 0}
              className="hidden lg:block absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 dark:text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Previous slide"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          {/* Carousel Container */}
          <div
            className="relative overflow-hidden rounded-2xl p-8"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex gap-4 transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * slideWidth}%)`,
              }}
            >
              {businessTypes.map(businessType => (
                <div
                  key={businessType.help_center_article_extra_json.slug}
                  className="flex-shrink-0"
                  style={{ width: `calc(${slideWidth}% - 1rem)` }}
                >
                  <BusinessTypeCard
                    title={
                      language === 'en'
                        ? businessType.help_center_article_title_en
                        : businessType.help_center_article_title_cn
                    }
                    description={
                      language === 'en'
                        ? businessType.help_center_article_introduction_en
                        : businessType.help_center_article_introduction_cn
                    }
                    imageIcon={
                      businessType.help_center_article_extra_json.image_icon
                    }
                    theme={businessType.help_center_article_extra_json.theme!}
                    showLearnMore={false}
                    className="h-full"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Right Arrow */}
          {maxIndex > 0 && (
            <button
              onClick={goToNext}
              disabled={currentIndex >= maxIndex}
              className="hidden lg:block absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 dark:text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Next slide"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}

          {/* Dots Navigation */}
          {maxIndex > 0 && (
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    index === currentIndex
                      ? 'w-8'
                      : 'opacity-30 hover:opacity-50'
                  }`}
                  style={{
                    backgroundColor:
                      index === currentIndex
                        ? themeColor
                        : 'var(--color-foreground-secondary)',
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Wave at top */}
      <WaveDivider
        position="bottom"
        backgroundColor={backgroundColor}
        theme={theme}
      />
    </section>
  );
}
